import { Component } from '@theme/component';

/**
 * @typedef {Object} ContactFormRefs
 * @property {HTMLInputElement} emailInput - The contact email field
 * @property {HTMLInputElement} emailMarketingInput - The "receive marketing emails" checkbox
 * @property {HTMLElement} countrySelector - The custom country dropdown wrapper
 * @property {HTMLButtonElement} countryToggle - The button that opens/closes the country list
 * @property {HTMLImageElement} countryFlag - The flag icon shown on the toggle button
 * @property {HTMLElement} countryCode - The dial code text shown on the toggle button
 * @property {HTMLElement} countryList - The list of selectable countries
 * @property {HTMLInputElement} phoneInput - The phone number field
 */

/**
 * Handles the contact form: subscribes the email to marketing in the
 * background (Shopify's native customer newsletter signup) when the
 * consent checkbox is checked, without interrupting the normal contact
 * form submission. Also drives the custom flag/dial-code country dropdown
 * and keeps the phone field's dial code in sync with the selection.
 *
 * @extends {Component<ContactFormRefs>}
 */
class ContactFormComponent extends Component {
  requiredRefs = [
    'emailInput',
    'emailMarketingInput',
    'countrySelector',
    'countryToggle',
    'countryFlag',
    'countryCode',
    'countryList',
    'phoneInput',
  ];

  #controller = new AbortController();

  /** @type {string} */
  #previousDialCode = '';

  connectedCallback() {
    super.connectedCallback();
    this.#previousDialCode = this.refs.countryCode.textContent?.trim() ?? '';

    document.addEventListener('click', this.#handleOutsideClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Subscribes the entered email to marketing (fire-and-forget) before the
   * normal contact form submission navigates the page away.
   */
  handleSubmit = () => {
    const { emailInput, emailMarketingInput } = this.refs;

    if (!emailMarketingInput.checked || !emailInput.value) return;

    const formData = new FormData();
    formData.append('form_type', 'customer');
    formData.append('utf8', '✓');
    formData.append('contact[email]', emailInput.value);
    formData.append('contact[tags]', 'newsletter');

    navigator.sendBeacon(`${Theme.routes.root_url}contact`, formData);
  };

  /** Opens/closes the country dropdown list. */
  toggleCountryList = () => {
    const { countryToggle, countryList } = this.refs;
    const isOpen = countryToggle.getAttribute('aria-expanded') === 'true';

    countryToggle.setAttribute('aria-expanded', String(!isOpen));
    countryList.hidden = isOpen;
  };

  /**
   * Applies the clicked country's flag/dial code to the toggle button,
   * updates the phone field's dial code prefix (unless the customer has
   * already typed a number beyond the prefix), and closes the list.
   * @param {Event} event
   */
  selectCountry = (event) => {
    const button = /** @type {HTMLElement} */ (event.target);
    const dialCode = button.dataset.dialCode;
    const flag = button.dataset.flag;
    if (!dialCode || !flag) return;

    const { countryToggle, countryFlag, countryCode, countryList, phoneInput } = this.refs;

    countryFlag.src = `https://cdn.shopify.com/static/images/flags/${flag}.svg`;
    countryCode.textContent = dialCode;

    if (phoneInput.value === '' || phoneInput.value === this.#previousDialCode) {
      phoneInput.value = dialCode;
    }

    this.#previousDialCode = dialCode;

    countryToggle.setAttribute('aria-expanded', 'false');
    countryList.hidden = true;
    countryToggle.focus();
  };

  /**
   * Closes the country list when clicking outside the dropdown.
   * @param {Event} event
   */
  #handleOutsideClick = (event) => {
    const { countrySelector, countryToggle, countryList } = this.refs;
    if (countryList.hidden) return;
    if (event.target instanceof Node && countrySelector.contains(event.target)) return;

    countryToggle.setAttribute('aria-expanded', 'false');
    countryList.hidden = true;
  };
}

if (!customElements.get('contact-form-component')) {
  customElements.define('contact-form-component', ContactFormComponent);
}
